import type { ScheduledCoupon } from '@/types/collection.types';
import { getUrgencyLevel, getDaysUntilDue } from '@/features/collection/utils/couponUrgency';
import { formatCurrencyMxn } from '@/features/collection/utils/couponFormatting';

export interface SortState {
    column: 'display_name' | 'credit_id' | 'coupon_number' | 'pending_amount_mxn' | 'due_date' | 'days';
    direction: 'asc' | 'desc' | null;
}

export interface AgendadosTableProps {
    coupons: ScheduledCoupon[];
    today: string;
    sort: SortState;
    onSortChange: (next: SortState) => void;
}

const URGENCY_CLASSES: Record<string, string> = {
    red: 'bg-red-500',
    yellow: 'bg-yellow-500',
    gray: 'bg-slate-500',
};

export function AgendadosTable({ coupons, today, sort, onSortChange }: AgendadosTableProps) {
    const handleHeaderClick = (column: SortState['column']) => {
        if (sort.column !== column) {
            onSortChange({ column, direction: 'asc' });
            return;
        }
        if (sort.direction === 'asc') onSortChange({ column, direction: 'desc' });
        else if (sort.direction === 'desc') onSortChange({ column: 'due_date', direction: 'asc' });
        else onSortChange({ column, direction: 'asc' });
    };

    const arrow = (column: SortState['column']) => {
        if (sort.column !== column) return '';
        if (sort.direction === 'asc') return ' ▲';
        if (sort.direction === 'desc') return ' ▼';
        return '';
    };

    if (coupons.length === 0) {
        return (
            <div className="rounded border border-slate-700 bg-slate-900 p-8 text-center text-sm text-slate-400">
                No hay cupones en este rango.
            </div>
        );
    }

    return (
        <table className="w-full text-sm">
            <thead className="border-b border-slate-700 text-left text-xs uppercase text-slate-400">
                <tr>
                    <th className="w-6"></th>
                    <th className="cursor-pointer px-3 py-2" onClick={() => handleHeaderClick('display_name')}>Cliente{arrow('display_name')}</th>
                    <th className="cursor-pointer px-3 py-2" onClick={() => handleHeaderClick('credit_id')}>Crédito{arrow('credit_id')}</th>
                    <th className="cursor-pointer px-3 py-2" onClick={() => handleHeaderClick('coupon_number')}># Cupón{arrow('coupon_number')}</th>
                    <th className="cursor-pointer px-3 py-2 text-right" onClick={() => handleHeaderClick('pending_amount_mxn')}>Monto{arrow('pending_amount_mxn')}</th>
                    <th className="cursor-pointer px-3 py-2" onClick={() => handleHeaderClick('due_date')}>Vencimiento{arrow('due_date')}</th>
                    <th className="cursor-pointer px-3 py-2 text-right" onClick={() => handleHeaderClick('days')}>Días{arrow('days')}</th>
                </tr>
            </thead>
            <tbody>
                {coupons.map((c, idx) => {
                    const urgency = getUrgencyLevel(c.due_date, today);
                    const days = getDaysUntilDue(c.due_date, today);
                    const creditSuffix = c.credit_id.slice(-4);
                    return (
                        <tr key={`${c.credit_id}-${c.coupon_number}`} className={idx % 2 === 0 ? 'bg-slate-900/40' : ''}>
                            <td className="px-3 py-2"><span className={`inline-block h-2 w-2 rounded-full ${URGENCY_CLASSES[urgency]}`} /></td>
                            <td className="px-3 py-2 text-slate-200">{c.client.display_name}</td>
                            <td className="px-3 py-2 font-mono text-xs text-slate-400">...{creditSuffix}</td>
                            <td className="px-3 py-2 text-slate-300">{c.coupon_number}/{c.coupon_total}</td>
                            <td className="px-3 py-2 text-right text-slate-200">{formatCurrencyMxn(c.pending_amount_mxn)}</td>
                            <td className="px-3 py-2 text-slate-300">{c.due_date}</td>
                            <td className="px-3 py-2 text-right text-slate-300">{days}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}
